import React, { useMemo, useLayoutEffect, useRef } from "react";
import { Datepicker } from "@mobiscroll/react";
import moment from "moment-timezone";
import {
  Stack,
  Grow,
  Divider,
  useColorScheme,
  useMediaQuery
} from "@mui/material";

import { MeetingAutobookInfo } from "../components/Meeting/MeetingAutobookInfo";
import { useSchedulerStore } from "../state/scheduler.store";

import { getSlotCountMobiscrollDatePickerLabels } from "../utils/slots";
import DatePickerSkeleton from "../components/Skeleton/DatePickerSkeleton";
import { ColumnViewSkeleton } from "../components/Skeleton/ColumnViewSkeleton";
import { first, last, random } from "lodash-es";
import { DateNavigationHeader } from "../components/Header/DateNavigationHeader";
import { AvailableSlotsGrid } from "../components/Availability/AvailableSlotsGrid";
import { useScreenSize } from "../hooks/useScreenSize";

import "../..//src/assets/mobiscroll.css";
import { disableDateNavButton, navigateWeek } from "../utils/dateNavigation";
import { AltChatUI } from "../components/Oliver/AltChatUI";
import { PoweredByBadge } from "../components/Branding/PoweredBy";
import { useAutobookDatePicker } from "../hooks/useDatePicker";
import { useTranslation } from "react-i18next";
import { getMobiscrollLocale } from "../i18n/mobiscroll";
import { useI18nLanguage } from "../hooks/i18nLanguage";
import { Participant } from "../services/api/types/availability.type";

interface IColumViewProps {
  currentlySelectedSlot: string | null;
  previouslySelectedTime: string | null;
  handleTimezoneChange: (tz: string) => void;
}

export const ColumnView: React.FC<IColumViewProps> = ({
  currentlySelectedSlot,
  previouslySelectedTime,
  handleTimezoneChange
}) => {
  const { mode: themeVariant } = useColorScheme();
  const { t, i18n } = useTranslation();
  const { selectedLanguageForMoment } = useI18nLanguage();

  const { isLargeScreen, isMediumScreen, isSmallScreen } = useScreenSize();

  const { minDate, maxDate, selectedDate } = useAutobookDatePicker();
  const {
    meetingLink,
    host,
    sessionData,
    actions,
    clientId,
    clientLogo,
    ui,
    loadingAvailability
  } = useSchedulerStore();

  const handleSlotClick = (slot: {
    hour: number;
    minute: number;
    date: number;
    participants: Participant[];
  }) => {
    actions.setSessionData({
      ...sessionData,
      selectedSlot: {
        hour: slot.hour,
        minute: slot.minute,
        date: slot.date,
        participants: slot.participants
      }
    });

    if (ui?.currentView === "column") {
      actions.setUiState({ ...ui, currentStep: "form" });
    }
  };

  const handleDatepickerChange = (event: { value: Date | null }) => {
    if (event.value?.valueOf() && ui?.currentStep === "initial") {
      actions.setSessionData({
        ...sessionData,
        prevSelectedSlot: sessionData?.prevSelectedSlot,
        selectedSlot: {
          date: moment(event.value.valueOf()).valueOf(),
          hour: null,
          minute: null
        }
      });
    }
  };

  const handlePreviousWeek = () =>
    navigateWeek("previous", sessionData, actions);
  const handleNextWeek = () => navigateWeek("next", sessionData, actions);

  const disableDateNavPrevButton = useMemo(
    () => disableDateNavButton("previous", sessionData),
    [sessionData?.selectedSlot, sessionData?.computedAvailability]
  );

  const disableDateNavNextButton = useMemo(
    () => disableDateNavButton("next", sessionData),
    [sessionData?.selectedSlot, sessionData?.computedAvailability]
  );

  return (
    <Grow in={true} style={{ transformOrigin: "1 2 2" }} timeout={250}>
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="flex-start"
        minWidth="100%"
        maxHeight="100dvh"
      >
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
          minWidth="100%"
          height="100%"
        >
          {/* Info Col */}
          {
            <Stack
              pl={3}
              pr={3} // Adjusted padding to account for scrollbar
              pt={1.5}
              justifyContent="space-between"
              flex={1}
              height="100dvh"
              overflow="auto"
              tabIndex={0}
              aria-label={t("ROOSTER.PUBLIC-APP.MEETING_INFO_REGION", {
                defaultValue: "Meeting information"
              })}
              sx={{
                minWidth: "380px",
                overflowY: "auto",
                overflowX: "hidden",
                paddingRight: "8px !important",
                "&::-webkit-scrollbar": {
                  width: "16px",
                  background: "transparent",
                  borderRadius: "20px" // Added for rounded track
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "transparent",
                  borderLeft: "10px solid transparent",
                  backgroundClip: "padding-box",
                  borderRadius: "20px" // Increased for rounder edges
                },
                "&:hover": {
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888",
                    borderLeft: "10px solid transparent",
                    backgroundClip: "padding-box"
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#555"
                  }
                }
              }}
            >
              <Stack>
                <MeetingAutobookInfo
                  isRoosterLink={meetingLink?.type === "rooster_links"}
                  clientLogo={clientLogo}
                  uiStep={ui?.currentStep ?? "loading"}
                  meetingDuration={meetingLink?.availability?.duration ?? null}
                  host={host ?? null}
                  selectedTimezone={sessionData?.selectedTimezone ?? null}
                  participants={meetingLink?.interviewers ?? []}
                  timeSlots={{
                    previouslySelectedTime,
                    currentlySelectedSlot
                  }}
                  handleTimezoneChange={handleTimezoneChange}
                />
              </Stack>

              <Stack px={-3} justifyContent="flex-end">
                {clientId &&
                sessionData?.computedAvailability?.length &&
                !loadingAvailability ? (
                  <Datepicker
                    showOuterDays={false}
                    selectMultiple={false}
                    onChange={handleDatepickerChange}
                    min={minDate}
                    max={maxDate}
                    display="inline"
                    calendarType="month"
                    valid={sessionData?.computedAvailability?.map(
                      (a) => a.date
                    )}
                    value={selectedDate}
                    labels={getSlotCountMobiscrollDatePickerLabels(
                      sessionData?.computedAvailability,
                      t
                    )}
                    themeVariant={themeVariant === "light" ? "light" : "dark"}
                    locale={getMobiscrollLocale(t, i18n.language)}
                  />
                ) : (
                  <DatePickerSkeleton rows={5} columns={7} />
                )}
              </Stack>
            </Stack>
          }

          {/* Slots Grid second col */}
          <Stack
            flex={7}
            width="100%"
            height="100dvh"
            sx={{
              pt: !isLargeScreen ? 4 : 0
            }}
            divider={<Divider orientation="horizontal" flexItem />}
          >
            <DateNavigationHeader
              selectedDate={selectedDate}
              isLoading={
                !clientId ||
                loadingAvailability ||
                !sessionData?.computedAvailability?.length
              }
              disablePrev={disableDateNavPrevButton}
              disableNext={disableDateNavNextButton}
              onPreviousWeek={handlePreviousWeek}
              onNextWeek={handleNextWeek}
              showNavigation={Boolean(
                clientId &&
                  sessionData?.computedAvailability &&
                  sessionData?.computedAvailability?.length > 0
              )}
            />
            <Stack
              direction="row"
              pt={2}
              pl={2}
              pr={2}
              spacing={2}
              maxHeight="93vh"
              overflow="auto"
              width="100%"
              tabIndex={0}
              role="region"
              aria-label={t("ROOSTER.PUBLIC-APP.TIME_SLOTS_REGION", {
                defaultValue: "Available time slots"
              })}
              sx={{
                "&::-webkit-scrollbar": {
                  width: "6px",
                  background: "transparent",
                  borderRadius: "10px"
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "transparent",
                  backgroundClip: "padding-box",
                  borderRadius: "10px"
                },
                "&:hover": {
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888",
                    backgroundClip: "padding-box"
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#555"
                  }
                }
              }}
            >
              {loadingAvailability ? (
                <ColumnViewSkeleton
                  columns={5}
                  minSlots={6}
                  maxSlots={random(6, 12)}
                />
              ) : (
                <AvailableSlotsGrid
                  computedAvailability={(
                    sessionData?.computedAvailability ?? []
                  ).map((day) => ({
                    ...day,
                    date: day.date
                  }))}
                  selectedSlotDate={selectedDate}
                  clientId={clientId}
                  loadingAvailability={loadingAvailability ?? true}
                  onSlotClick={handleSlotClick}
                />
              )}
            </Stack>
          </Stack>
          <AltChatUI view="columnView" showChatIcon={isSmallScreen} />
        </Stack>
        <PoweredByBadge show translucent />
      </Stack>
    </Grow>
  );
};
